import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Flame, Globe, Scroll } from 'lucide-react';
import { useTranslation } from '../i18n';
import SectionTabs from './SectionTabs';

// URL → tab key. Home is the index, so anything unrecognised reads as Feed.
const TAB_KEYS = { '/stories': 'stories', '/journal': 'journal' };

/**
 * HomeLayout — Feed · Stories · Journal.
 *
 * Feed used to be the landing page while being absent from the bottom nav,
 * reachable only by tapping the logo. Home fixes that: the logo now points at
 * a destination that exists in the bar, and Stories and Journal give up their
 * own bottom-nav slots to sit alongside it.
 *
 * Children render their own PageHeader — Feed's is a personalised greeting and
 * Journal's is a date navigator, so there is nothing worth hoisting. This
 * layout contributes the tab strip and nothing else.
 */
export default function HomeLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = TAB_KEYS[pathname] || 'feed';

  const tabs = [
    { to: '/',        end: true, label: t('nav.feed'),    Icon: Flame  },
    { to: '/stories',            label: t('nav.stories'), Icon: Globe  },
    { to: '/journal',            label: t('nav.journalShort') || t('nav.journal'), Icon: Scroll },
  ];

  // The strip is handed to the child rather than rendered here, so it can sit
  // *under* the page title the way Prayer's and People's do. Those sections own
  // their header in the layout; Home's children own theirs — Feed's is a
  // personalised greeting and Journal's carries a date navigator, neither of
  // which is worth losing to hoist the title up here.
  const sectionTabs = <SectionTabs tabs={tabs} ariaLabel={t('nav.home')} />;

  return (
    <div className={`home-section home-section--${active}`}>
      <Outlet context={{ sectionTabs }} />
    </div>
  );
}
